import { Locations } from "../assets/Locations";
import { createContext, useState } from "react";
import { LaunderUpgrades } from "../assets/Upgrades";
import { useInterval } from "../helper/TimerHelper";
import * as Entity from "../helper/EntityHelper";
import * as EntityBuilder from "../EntityBuilder";
import hitkit from "../assets/music/HitKit_LD50.mp3";
import { getRandomInt, getRandomElement } from "../helper/MathHelper";
import { mkMessage } from "../helper/MessageHelper";
import { debugLog } from "../helper/DevHelper";

export const GameState = createContext();

// FIXME split up state object, refactor impure functions calling setState into pure functions
// that mutate a state object and return a different state object, refactor pure "game" functions
// to their own module

export const GameStateProvider = (props) => {
  const setGameMode = (mode) => {
    setState((prevState) => ({
      ...prevState,
      gameMode: mode,
    }));
  };

  const setSelectedLocation = (id) => {
    setState((prevState) => ({
      ...prevState,
      selectedLocation: id,
    }));
  };

  const setLocationActive = (id, active) => {
    setState((prevState) => ({
      ...prevState,
      locations: prevState.locations.map((loc, idx) => {
        if (idx === id) {
          loc.active = active;
        }
        return loc;
      }),
    }));
  };

  const countActiveLocations = () => {
    return state.locations
      .map((loc) => {
        return loc.active ? 1 : 0;
      })
      .reduce((curSum, val) => {
        return curSum + val;
      }, 0);
  };

  const setLocationMoney = (id, newMoney) => {
    setState((prevState) => ({
      ...prevState,
      locations: prevState.locations.map((loc, idx) => {
        if (idx === id) {
          loc.money = newMoney;
        }
        return loc;
      }),
    }));
  };

  const addLocationMoney = (id, moreMoney) => {
    setState((prevState) => ({
      ...prevState,
      locations: prevState.locations.map((loc, idx) => {
        if (idx === id) {
          loc.money += moreMoney;
        }
        return loc;
      }),
    }));
  };

  const raidLocation = (id, raid, raidMod) => {
    setState((prevState) => {
      let inactiveLocations = prevState.inactiveLocations;
      let msg = null;
      let seizeMoney = 0;
      let selectedLocation = prevState.selectedLocation;
      let transferFrom = prevState.transferFrom;
      const newLocations = prevState.locations.map((loc, idx) => {
        if (idx === id) {
          seizeMoney = Math.floor(loc.money * raid.moneyMod * raidMod);
          const heatDecr = Math.floor(raid.heatMod * loc.heat * raidMod);
          loc.money -= seizeMoney;
          loc.baseHeat = Math.max(0, loc.baseHeat - heatDecr);
          if (raid.kill) {
            loc.active = false;
            inactiveLocations++;

            // deselect the killed location if necessary
            if (id === selectedLocation) {
              selectedLocation = -1;
            }
            if (id === transferFrom) {
              transferFrom = -1;
            }
          }
          const msgText = raid.msg + loc.name;
          msg = mkMessage(msgText, 3, "bad");
        }
        return loc;
      });

      let newMessages = prevState.messages;
      if (msg !== null) {
        newMessages = newMessages.concat([msg]);
      }

      return {
        ...prevState,
        inactiveLocations: inactiveLocations,
        locations: newLocations,
        messages: newMessages,
        seizedMoney: prevState.seizedMoney + seizeMoney,
        selectedLocation: selectedLocation,
        transferFrom: transferFrom,
      };
    });
  };

  const spawnTransfer = (fromId, toId, amount) => {
    spawnEntity(EntityBuilder.makeTransfer(state, fromId, toId, amount));
  };

  const spawnEntity = (entity) => {
    setState((prevState) => {
      const id = prevState.nextEntityId;
      const newEntities = prevState.entities.concat([
        {
          ...entity,
          id: id,
        },
      ]);
      return {
        ...prevState,
        entities: newEntities,
        nextEntityId: id + 1,
      };
    });
  };

  const spawnAgent = (locId) => {
    spawnEntity(EntityBuilder.makeAgent(state, locId));
  };

  const updateEntities = () => {
    setState((prevState) => ({
      ...prevState,
      entities: Entity.updateEntities(prevState, prevState.entities),
    }));
  };

  const updateLocations = () => {
    // location revenues
    const revenueChance = 5;
    const baseRevenue = 1000;
    const revenueMax = 10000;
    let scoreIncrease = 0;

    const calculateHeat = (money) => {
      if (money === 0) {
        return 0;
      } else {
        return Math.floor(Math.log10(money * 2));
      }
    };

    const calculateRevenue = (inactiveLocations) => {
      const revenue = getRandomInt(revenueMax);
      const bonusRevenue =
        500 * getRandomInt(Math.pow(inactiveLocations + 1, 2));
      return baseRevenue + bonusRevenue + revenue;
    };

    setState((prevState) => ({
      ...prevState,
      locations: prevState.locations.map((loc) => {
        if (loc.active) {
          const upgradeHeat = LaunderUpgrades[loc.upgrades.launder].heat;

          if (getRandomInt(1000) < revenueChance) {
            loc.money += calculateRevenue(prevState.inactiveLocations);
            loc.heat = Math.floor(
              (calculateHeat(loc.money) + Math.sqrt(loc.baseHeat)) * upgradeHeat
            );
          }

          const launderRate = LaunderUpgrades[loc.upgrades.launder].rate;
          if (loc.money > launderRate) {
            loc.money -= launderRate;
            scoreIncrease += launderRate;
          } else if (loc.money > 0) {
            scoreIncrease += loc.money;
            loc.money = 0;
          }
        }
        return loc;
      }),
      score: prevState.score + scoreIncrease,
    }));
  };

  const updateHeat = () => {
    // recalculate global heat
    setState((prevState) => {
      const calcHeat = () => {
        const activeLocations = prevState.locations.filter((loc) => {
          return loc.active;
        });

        const heats = activeLocations.map((loc) => {
          return loc.heat;
        });

        const totalHeat = heats.reduce((pv, cv) => {
          return pv + cv;
        }, 0.0);

        const avgHeat = totalHeat / heats.length;

        const locHeat = Math.max(1.0, prevState.inactiveLocations / 2);

        return Math.floor(prevState.bonusHeat + avgHeat + locHeat);
      };
      const newHeat = calcHeat();
      if (prevState.heat !== newHeat) {
        debugLog("Global heat is now " + newHeat);
      }
      return {
        ...prevState,
        heat: newHeat,
      };
    });
  };

  const sendMessageAt = (position, text, duration = 1, type = "good") => {
    const msg = {
      text: text,
      expires: Date.now() + duration * 1000,
      type: type,
      position: position,
    };

    setState((prevState) => ({
      ...prevState,
      mapMessages: prevState.mapMessages.concat([msg]),
    }));
  };

  const sendMessage = (text, duration = 3, type = "good") => {
    const msg = mkMessage(text, duration, type);
    setState((prevState) => ({
      ...prevState,
      messages: prevState.messages.concat([msg]),
    }));
  };

  const checkMessages = () => {
    const now = Date.now();
    setState((prevState) => ({
      ...prevState,
      messages: prevState.messages.filter((msg) => {
        return now < msg.expires;
      }),
      mapMessages: prevState.mapMessages.filter((msg) => {
        return now < msg.expires;
      }),
    }));
  };

  const startTransfer = (id, percent) => {
    // Begin new transfer
    state.transferFrom = id;
    state.transferPercent = percent;
    state.selectedLocation = -1;
    setState((prevState) => ({
      ...prevState,
      transferFrom: id,
      transferPercent: percent,
      selectedLocation: -1,
    }));
  };

  const finishTransfer = (id) => {
    if (id !== state.transferFrom) {
      // Launch the transfer
      const amount = Math.floor(
        state.locations[state.transferFrom].money * state.transferPercent
      );
      debugLog(
        "Transfer from " + state.transferFrom + " to " + id + " - $" + amount
      );

      // decrement the from balance
      addLocationMoney(state.transferFrom, -amount);
      // initiate the transfer
      spawnTransfer(state.transferFrom, id, amount);
      // update location heat
      setState((prevState) => ({
        ...prevState,
        locations: prevState.locations.map((loc, idx) => {
          if (idx === state.transferFrom) {
            const transferHeatMod = 0.75;
            const transferHeatMin = 1;
            const newHeat = Math.max(
              transferHeatMin,
              Math.floor(Math.log10(amount) * transferHeatMod)
            );
            loc.baseHeat += newHeat;
          }
          return loc;
        }),
      }));
    }
    setState((prevState) => ({
      ...prevState,
      transferFrom: -1,
      transferPercent: 0,
    }));
  };

  const updateAgents = () => {
    setState((prevState) => {
      const numAgents = () => {
        const heatAgentMod = 1;
        const minAgents = 2;
        const locationAgentMod = 0;
        const locationAgents = Math.max(
          0,
          Math.log2(prevState.inactiveLocations) + locationAgentMod
        );
        const heatAgents = Math.pow(prevState.heat + heatAgentMod, 1.1) / 4;
        return (
          prevState.timeAgents +
          Math.max(minAgents, Math.floor(heatAgents + locationAgents))
        );
      };
      const agents = numAgents();
      const newAgentsNum = Math.max(agents - prevState.agents, 0);
      if (newAgentsNum > 0) {
        debugLog("Spawning " + newAgentsNum + " agents");
      }
      const newAgents = [];
      for (let i = 0; i < newAgentsNum; ++i) {
        const activeLocations = prevState.locations.filter((loc) => {
          return loc.active;
        });
        const loc = getRandomElement(activeLocations).id;
        const entity = EntityBuilder.makeAgent(prevState, loc);
        entity.trackingState.sleep = true;
        const ts = entity.trackingState;
        setTimeout(() => {
          ts.sleep = false;
        }, entity.trackingSleep);
        newAgents.push(entity);
      }
      return {
        ...prevState,
        agents: agents,
        entities: prevState.entities.concat(newAgents),
      };
    });
  };

  const markTransferDead = (transferId) => {
    setState((prevState) => {
      let transferMoney = 0;
      const newEntities = prevState.entities.map((entity) => {
        if (entity.face === "transfer") {
          if (entity.id === transferId) {
            entity.dead = true;
            transferMoney = entity.money;
          }
        }
        return entity;
      });
      return {
        ...prevState,
        entities: newEntities,
        seizedMoney: prevState.seizedMoney + transferMoney,
      };
    });
  };

  const playSong = (song) => {
    if (!(song in state.music)) {
      debugLog("No such song: " + song);
      return;
    }

    stopMusic();

    // Play selected song
    state.music[song].play();
    state.music[song].loop = true;
  };

  const stopMusic = () => {
    // FIXME impure state modification
    Object.keys(state.music).forEach((key, idx) => {
      state.music[key].pause();
      state.music[key].currentTime = 0;
    });
  };

  const upgradeLaunder = (id) => {
    setState((prevState) => {
      const newLocations = prevState.locations.map((loc) => {
        if (loc.id === id) {
          const curLaunder = loc.upgrades.launder;
          if (curLaunder < LaunderUpgrades.length - 1) {
            const cost = LaunderUpgrades[curLaunder + 1].cost;
            if (loc.money >= cost) {
              loc.money -= cost;
              loc.upgrades.launder++;
            }
          }
        }
        return loc;
      });

      return {
        ...prevState,
        locations: newLocations,
      };
    });
  };

  const initialState = {
    // State variables
    gameMode: 0, // 0 = main menu, 1 = instructions, 2 = main game, 3 = game over
    locations: Locations.map((loc, idx) => {
      return {
        active: true,
        upgrades: {
          stealth: 0,
          revenue: 0,
          launder: 0,
        },
        money: 0,
        heat: 0,
        baseHeat: 0,
        key: loc.name, // unique constraint
        id: idx,
        ...loc,
      };
    }),
    selectedLocation: -1,
    score: 0,
    heat: 0,
    seizedMoney: 0,
    transferFrom: -1,
    transferPercent: 0,
    entities: [],
    nextEntityId: 0,
    messages: [],
    mapMessages: [],
    inactiveLocations: 0,
    agents: 0,
    timeAgents: 0,
    bonusHeat: 1,
    music: {
      hitkit: new Audio(hitkit),
    },

    // Functions for interacting with the state
    setGameMode: setGameMode,
    setSelectedLocation: setSelectedLocation,
    setLocationActive: setLocationActive,
    setLocationMoney: setLocationMoney,
    startTransfer: startTransfer,
    finishTransfer: finishTransfer,
    spawnEntity: spawnEntity,
    updateEntities: updateEntities,
    addLocationMoney: addLocationMoney,
    spawnTransfer: spawnTransfer,
    spawnAgent: spawnAgent,
    sendMessage: sendMessage,
    sendMessageAt: sendMessageAt,
    markTransferDead: markTransferDead,
    playSong: playSong,
    stopMusic: stopMusic,
    raidLocation: raidLocation,
    upgradeLaunder: upgradeLaunder,
  };

  const [state, setState] = useState(initialState);

  useInterval(() => {
    if (state.gameMode === 2) {
      updateEntities();
      updateLocations();
      updateHeat();
      updateAgents();
      checkMessages();
      if (countActiveLocations() === 0) {
        state.stopMusic();
        setGameMode(3);
      }
    }
  }, 1000 / 30);

  return (
    <GameState.Provider value={state}>{props.children}</GameState.Provider>
  );
};
